import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription,
  DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Loader2, AlertTriangle } from 'lucide-react';

/**
 * CustomerCancelBookingDialog
 *
 * Replaces the bare `window.confirm` on customer cancellation. Captures
 * an OPTIONAL `reason` (free-form, max ~280 chars to keep it tweet-sized).
 * Reason lands on `booking.cancellation_reason` and the customer email
 * for the audit trail; empty → not stored.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange(open): controlled toggle (false closes dialog)
 *  - bookingRef: short reference string for the header (e.g. "#1234abcd")
 *  - submitting: boolean — parent's "cancelling…" state
 *  - onConfirm(reason): called when user clicks Yes. parent does the API call.
 */
export default function CustomerCancelBookingDialog({
  open, onOpenChange, bookingRef, submitting = false, onConfirm,
}) {
  const [reason, setReason] = useState('');

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    await onConfirm(trimmed || null);
    // parent closes on success; we just clear local state for next open
    setReason('');
  };

  const handleOpenChange = (next) => {
    if (!next && !submitting) setReason('');
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="customer-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Cancel this booking?
          </DialogTitle>
          <DialogDescription>
            Your court slot will be released and we'll email you a confirmation.
            {bookingRef ? <> Booking <span className="font-mono">{bookingRef}</span></> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="cancel-reason" className="text-sm">
            Reason <span className="text-muted-foreground font-normal">(optional)</span>
          </Label>
          <Textarea
            id="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 280))}
            placeholder="e.g. Schedule conflict, found another time, etc."
            rows={3}
            data-testid="customer-cancel-reason"
          />
          <div className="text-[10px] text-muted-foreground text-right">{reason.length}/280</div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
            data-testid="customer-cancel-keep-btn"
          >
            Keep Booking
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={submitting}
            data-testid="customer-cancel-confirm-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancelling…</>
            ) : 'Yes, cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
